import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PartnerStatus,
  ChannelStatus,
  ChannelType,
  PartnerType,
  PreferredContactMethod,
  LocationRequestType,
  LocationRequestStatus,
} from '../../constants/partners';
import { ElectionCategory } from '../../constants/categories';
import { KycItem, PartnerKycStatus } from '../../constants/kyc';
import { PartnerWalletInfo } from '../../api/walletApi';

export interface PartnerLocationRef {
  id: number;
  // Can be null if the district/samiti was removed from the list since
  name: string | null;
}

export type PlaceRef = PartnerLocationRef;

export interface LocationValue {
  state: string;
  district: PlaceRef;
  samiti: PlaceRef;
}

export interface TerritoryValue {
  districts: PlaceRef[];
  samitis: PlaceRef[];
}

export interface PartnerLocationRequestItem {
  id: string;
  request_type: LocationRequestType;
  status: LocationRequestStatus;
  current: LocationValue | TerritoryValue;
  requested: LocationValue | TerritoryValue;
  reason: string;
  reject_reason: string | null;
  created_at: string;
  decided_at: string | null;
  cancelled_at: string | null;
  partner: {
    id: string;
    name: string;
    mobile: string;
    status: PartnerStatus;
  };
  decided_by: string | null;
}

export interface PartnerChannelItem {
  channel: ChannelType;
  status: ChannelStatus;
  // Only on GET /partner/me / login answers; the owner list/detail use status_changed_at instead
  status_reason?: string | null;
  status_changed_at?: string | null;
}

export type PromoCodeChannel = 'creator' | 'channel_partner';

export interface PartnerPromoCodeItem {
  id: string;
  code: string;
  // Migration 0029: one code per partner for every plan category, so a current code has
  // category null. Only a code replaced before that change can still carry a category.
  category: ElectionCategory | null;
  // The one channel the partner's orders are counted in right now (channel_partner when it
  // is approved, else creator). null when neither applies.
  channel?: PromoCodeChannel | null;
  // Every code channel the partner selected, in the order creator, channel_partner
  channels?: PromoCodeChannel[];
  discount_type: 'percentage' | 'flat';
  discount_value: string; // decimal string, e.g. "20.00"
  is_active: boolean;
  created_at: string;
  // "partner_status" (auto, comes back on re-approval) | "owner" (by hand) | "replaced" (Phase 9) | null (active)
  deactivated_reason?: 'partner_status' | 'owner' | 'replaced' | null;
  source?: 'auto' | 'custom' | null;
  is_current?: boolean;
  replaced_at?: string | null;
  replaces_id?: string | null;
}

export interface PartnerAuditEntry {
  id: string;
  actor_type: 'partner' | 'owner' | 'system';
  // The owner's email for owner actions; the partner id for partner actions; null for system
  actor_id: string | null;
  action: string;
  // Phase 3 adds channel_partner_details, territory, photo; Phase 4 adds kyc; Phase 10 adds location_request; Phase 11a adds wallet; Phase 11b adds material_order, wallet_ledger
  entity_type:
    | 'profile'
    | 'channel'
    | 'promo_code'
    | 'channel_partner_details'
    | 'territory'
    | 'photo'
    | 'kyc'
    | 'location_request'
    | 'wallet_recharge'
    | 'wallet_adjustment'
    | 'wallet'
    | 'material_order'
    | 'wallet_ledger';
  entity_id: string;
  before: Record<string, any> | null;
  after: Record<string, any> | null;
  reason: string | null;
  created_at: string;
}

// One place in a Channel Partner's sales territory: a whole district (samiti null)
// or one panchayat samiti within it
export interface TerritoryEntry {
  district: PartnerLocationRef;
  samiti: PartnerLocationRef | null;
}

// channel_details.channel_partner — null when the partner didn't select that channel.
// Owner Panel is read-only: there is no route to change any of this.
export interface ChannelPartnerDetails {
  display_name: string | null;
  // What to show: display_name, or the registered name while none is saved
  display_name_effective: string;
  partner_type: PartnerType | null;
  business_name: string | null;
  business_address: string | null;
  gstin: string | null;
  preferred_contact_method: PreferredContactMethod | null;
  territory: TerritoryEntry[];
  updated_at: string | null;
}

export interface CreatorSocialLink {
  id: string;
  platform: {
    id: string;
    name: string;
    is_active: boolean;
  };
  url: string;
  created_at: string;
  updated_at: string;
}

export interface CreatorDetails {
  display_name: string | null;
  display_name_effective: string;
  bio: string | null;
  preferred_language: string | null;
  content_category: string | null;
  social_links: CreatorSocialLink[];
  updated_at: string | null;
}

export interface ProfileCompletionItem {
  key: string;
  label: string;
  done: boolean;
  // ["all"] for everyone, else the channels this item belongs to
  required_for: string[];
}

export interface ProfileCompletion {
  // done items x 100 / all items, rounded down. Informational only — never gates a feature.
  percent: number;
  items: ProfileCompletionItem[];
}

// Phase 4. Counts/flags only — the full per-item review is a separate call (GET
// /owner/partners/{id}/kyc). Follows the CURRENT kyc settings.
export interface PartnerKycSummary {
  kyc_status: PartnerKycStatus;
  verified_count: number;
  required_count: number;
  payout_ready: boolean;
}

// Extra fields on the detail's kyc block only (not on the list row)
export interface PartnerKycDetailSummary extends PartnerKycSummary {
  kyc_required_for_payout: boolean;
  missing_for_payout: KycItem[];
}

// Row shape for GET /owner/partners
export interface PartnerListItem {
  id: string;
  name: string;
  mobile: string;
  state: string;
  district: PartnerLocationRef | null;
  samiti: PartnerLocationRef | null;
  status: PartnerStatus;
  channels: PartnerChannelItem[];
  created_at: string;
  updated_at: string;
  status_changed_at: string;
  // Phase 4
  kyc: PartnerKycSummary;
  // Migration 0035: set while the partner is soft-deleted (null otherwise)
  deleted_at?: string | null;
  deleted_reason?: string | null;
}

// GET /owner/partners/{id} adds the promo codes, the audit log (Phase 4: paged, newest
// 100 rows, kyc_viewed rows left out), and (Phase 3) the photo, Channel Partner details
// and profile completion checklist
export interface PartnerDetail extends Omit<PartnerListItem, 'kyc'> {
  promo_codes: PartnerPromoCodeItem[];
  audit_log: PartnerAuditEntry[];
  audit_log_total: number;
  audit_log_limit: number;
  audit_log_offset: number;

  // Signed, short-lived (5 min by default); null without a photo or while photos are off
  photo_url: string | null;
  photo_uploaded_at: string | null;
  channel_details: {
    channel_partner: ChannelPartnerDetails | null;
    creator?: CreatorDetails | null;
  };
  profile_completion: ProfileCompletion;

  // Phase 4
  kyc: PartnerKycDetailSummary;

  // Phase 10: Pending Location Change Requests (Owner shape)
  pending_location_requests?: {
    location: PartnerLocationRequestItem | null;
    territory: PartnerLocationRequestItem | null;
  } | null;

  // Phase 11a: Material Wallet (Owner shape)
  wallet?: PartnerWalletInfo | null;

  // Phase 11b: Material Orders (Owner shape)
  material?: PartnerMaterialSummary | null;
}

export interface PartnerMaterialSummary {
  orders: number;
  orders_by_state: {
    awaiting_upload: number;
    parsing: number;
    parse_failed: number;
    quoted: number;
    confirmed: number;
    generating: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  total_spent: string;
}

export interface PartnerCounts {
  all: number;
  pending: number;
  approved: number;
  rejected: number;
  suspended: number;
  // Migration 0035: soft-deleted partners; the other counts leave them out
  deleted: number;
}

export interface PartnerState {
  items: PartnerListItem[];
  // Tab badges: ignore the filters
  counts: PartnerCounts;
  // Rows matching the current filters, used for paging
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;

  selected: PartnerDetail | null;
  selectedLoading: boolean;
  selectedError: string | null;
}

const initialState: PartnerState = {
  items: [],
  counts: { all: 0, pending: 0, approved: 0, rejected: 0, suspended: 0, deleted: 0 },
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,

  selected: null,
  selectedLoading: false,
  selectedError: null,
};

const partnerSlice = createSlice({
  name: 'partners',
  initialState,
  reducers: {
    setPartnerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPartnerError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPartnerPage: (
      state,
      action: PayloadAction<{
        items: PartnerListItem[];
        counts: PartnerCounts;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, counts, total, limit, offset } = action.payload;
      state.items = items;
      state.counts = counts;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
    setSelectedPartnerLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedLoading = action.payload;
    },
    setSelectedPartnerError: (state, action: PayloadAction<string | null>) => {
      state.selectedError = action.payload;
    },
    setSelectedPartner: (state, action: PayloadAction<PartnerDetail | null>) => {
      const p = action.payload;
      // The detail page maps over these lists; a response without them must not crash it
      state.selected = p
        ? {
            ...p,
            promo_codes: p.promo_codes || [],
            audit_log: p.audit_log || [],
            channels: p.channels || [],
          }
        : p;
      state.selectedLoading = false;
    },
    // After approve/reject/suspend the server returns the refreshed detail;
    // also patch the summary row in the list so the table doesn't look stale
    updatePartnerInListReducer: (state, action: PayloadAction<PartnerListItem>) => {
      const idx = state.items.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    },
    // Soft delete / restore / hard delete moves the partner out of the list it is shown in
    removePartnerFromListReducer: (state, action: PayloadAction<string>) => {
      const before = state.items.length;
      state.items = state.items.filter((p) => p.id !== action.payload);
      if (state.items.length !== before) state.total = Math.max(0, state.total - 1);
    },
    // Appends an older page of the audit log (Phase 4: paged, "Load older" button)
    appendPartnerAuditLogReducer: (
      state,
      action: PayloadAction<{
        audit_log: PartnerAuditEntry[];
        audit_log_total: number;
        audit_log_limit: number;
        audit_log_offset: number;
      }>
    ) => {
      if (!state.selected) return;
      state.selected.audit_log = [...state.selected.audit_log, ...action.payload.audit_log];
      state.selected.audit_log_total = action.payload.audit_log_total;
      state.selected.audit_log_limit = action.payload.audit_log_limit;
      state.selected.audit_log_offset = action.payload.audit_log_offset;
    },
    // Partner Phase Nine: custom promo code created or replaced
    addOrReplacePartnerPromoCode: (
      state,
      action: PayloadAction<{ code: PartnerPromoCodeItem; replaced: PartnerPromoCodeItem | null }>
    ) => {
      if (!state.selected) return;
      const { code, replaced } = action.payload;
      let existingCodes = [...state.selected.promo_codes];

      if (replaced) {
        const rIdx = existingCodes.findIndex((c) => c.id === replaced.id);
        if (rIdx !== -1) {
          existingCodes[rIdx] = replaced;
        } else {
          existingCodes.push(replaced);
        }
      }

      const cIdx = existingCodes.findIndex((c) => c.id === code.id);
      if (cIdx !== -1) {
        existingCodes[cIdx] = code;
      } else {
        existingCodes = [code, ...existingCodes];
      }

      state.selected.promo_codes = existingCodes;
    },
    updatePartnerPromoCodeStatus: (
      state,
      action: PayloadAction<{ id: string; is_active: boolean; deactivated_reason?: any }>
    ) => {
      if (!state.selected) return;
      const item = state.selected.promo_codes.find((c) => c.id === action.payload.id);
      if (item) {
        item.is_active = action.payload.is_active;
        if (action.payload.deactivated_reason !== undefined) {
          item.deactivated_reason = action.payload.deactivated_reason;
        } else if (!action.payload.is_active) {
          item.deactivated_reason = 'owner';
        } else {
          item.deactivated_reason = null;
        }
      }
    },
    // Phase 10: Clear or update a pending location request on the partner detail
    updatePendingLocationRequest: (
      state,
      action: PayloadAction<{ request_type: LocationRequestType; request: PartnerLocationRequestItem | null }>
    ) => {
      if (!state.selected) return;
      if (!state.selected.pending_location_requests) {
        state.selected.pending_location_requests = { location: null, territory: null };
      }
      state.selected.pending_location_requests[action.payload.request_type] = action.payload.request;
    },
  },
});

export const {
  setPartnerLoading,
  setPartnerError,
  setPartnerPage,
  setSelectedPartnerLoading,
  setSelectedPartnerError,
  setSelectedPartner,
  updatePartnerInListReducer,
  removePartnerFromListReducer,
  appendPartnerAuditLogReducer,
  addOrReplacePartnerPromoCode,
  updatePartnerPromoCodeStatus,
  updatePendingLocationRequest,
} = partnerSlice.actions;

export default partnerSlice.reducer;
